import { motion } from "framer-motion";
import { ShieldCheck, Clock, Sparkles, ArrowUpRight } from "lucide-react";
import { whatsappUrl, trackWaClick } from "../lib/whatsapp";

const ease = [0.22, 1, 0.36, 1];

// Module-level animation configs — referentially stable across renders
const cardInitial = { opacity: 0, y: 24 };
const cardAnimate = { opacity: 1, y: 0 };
const cardTransition = { duration: 0.7, ease, delay: 0.1 };
const ctaWhileTap = { scale: 0.98 };
const ctaWhileHover = { y: -2 };

// Simple inline WhatsApp glyph — stays on-brand (pure green, no Meta logo required).
const WhatsAppGlyph = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z"/>
    <path d="M20.52 3.449C18.24 1.17 15.24 0 12.04 0 5.46 0 .14 5.32.14 11.9c0 2.09.54 4.13 1.57 5.94L.03 24l6.36-1.67a11.89 11.89 0 0 0 5.64 1.44h.01c6.58 0 11.9-5.32 11.9-11.9 0-3.18-1.26-6.16-3.42-8.42ZM12.04 21.8h-.01a9.88 9.88 0 0 1-5.03-1.38l-.36-.21-3.78 1 1.01-3.68-.24-.38A9.85 9.85 0 0 1 2.13 11.9C2.14 6.45 6.6 2 12.05 2c2.66 0 5.15 1.04 7.03 2.92a9.83 9.83 0 0 1 2.91 7.01c-.01 5.46-4.46 9.87-9.95 9.87Z"/>
  </svg>
);

export default function WhatsAppHeroCard() {
  return (
    <motion.div
      initial={cardInitial}
      animate={cardAnimate}
      transition={cardTransition}
      className="relative bg-brand-surface rounded-3xl border border-brand-line p-6 md:p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] overflow-hidden"
      data-testid="whatsapp-hero-card"
    >
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-energy/25 blur-[90px]" />

      <div className="relative">
        {/* Tag */}
        <div className="inline-flex items-center gap-1.5 bg-brand-offwhite rounded-full px-3 py-1.5 text-[11px] font-semibold text-brand-ink">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-energy animate-pulse" />
          Kostenlos · Antwort in 24h
        </div>

        <h2 className="mt-5 font-heading text-3xl md:text-4xl font-black tracking-tighter text-brand-ink leading-[1.05]">
          Rechnung senden.<br />Wir übernehmen den Rest.
        </h2>
        <p className="mt-3 text-sm md:text-base text-brand-muted leading-relaxed max-w-md">
          Ein Klick öffnet WhatsApp mit einer vorbereiteten Nachricht. Du schickst uns ein Foto
          oder PDF deiner aktuellen Strom- oder Gasrechnung – wir melden uns mit deinem persönlichen Angebot.
        </p>

        {/* Primary CTA — WhatsApp */}
        <motion.a
          href={whatsappUrl()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWaClick("hero_card")}
          whileTap={ctaWhileTap}
          whileHover={ctaWhileHover}
          className="mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full py-4 px-6 text-base font-semibold text-white shadow-[0_12px_36px_-8px_rgba(0,200,83,0.55)] transition-all"
          style={{ background: 'linear-gradient(135deg, #00C853 0%, #00E676 100%)' }}
          data-testid="hero-whatsapp-cta"
        >
          <WhatsAppGlyph className="w-5 h-5" />
          Jetzt per WhatsApp starten
          <ArrowUpRight className="w-4 h-4 arrow-shift" strokeWidth={2.4} />
        </motion.a>

        {/* Sub-line with trust cues */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          <TrustCue icon={ShieldCheck} label="DSGVO" />
          <TrustCue icon={Clock} label="24h" />
          <TrustCue icon={Sparkles} label="0 €" />
        </div>

        <p className="mt-5 text-[11px] text-brand-muted leading-relaxed">
          Wir speichern keine Daten ohne deine Zustimmung. Mit dem Öffnen von WhatsApp stimmst du
          der Nutzung dieses Dienstes (Meta Platforms Ireland Ltd.) zu. Details in der{" "}
          <a href="/datenschutz" className="underline text-brand-ink hover:text-brand-energy">Datenschutzerklärung</a>.
        </p>
      </div>
    </motion.div>
  );
}

function TrustCue({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 bg-brand-paper border border-brand-line rounded-xl px-3 py-2">
      <Icon className="w-3.5 h-3.5 text-brand-energy" strokeWidth={2.4} />
      <span className="text-[11px] font-semibold text-brand-ink tracking-tight">{label}</span>
    </div>
  );
}
